/*
 * Copyright (c) 2018 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschuetzt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulaessig. Dies gilt
 * insbesondere fuer Vervielfaeltigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht uebergeben ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 *
 */
import moment from 'moment';
import DateUtil from '../utils/DateUtil';
import {TSSchadenArt} from './enums/TSSchadenArt';
import {TSVersicherungsbeitragStatus} from './enums/TSVersicherungsbeitragStatus';
import {IHasUnconnectedDocuments} from './IHasUnconnectedDocuments';
import TSAbstractEntity from './TSAbstractEntity';
import TSBeitrag from './TSBeitrag';
import TSGemeinde from './TSGemeinde';
import TSGesuchsteller from './TSGesuchsteller';
import {TSSerializable} from './TSSerializable';
import TSVersicherung from './TSVersicherung';

export default class TSVersicherungsbeitrag extends TSAbstractEntity implements TSSerializable<TSVersicherungsbeitrag>, IHasUnconnectedDocuments {

    private _schadendatum: moment.Moment | undefined;

    private _bemerkung: string;

    private _status: TSVersicherungsbeitragStatus;

    private _versicherung: TSVersicherung;

    private _gesuchsteller: TSGesuchsteller;

    private _policeNummer: string | undefined;
    private _externalSchadenNr: string;
    private _schadenArt: TSSchadenArt;
    private _gemeinde: TSGemeinde | undefined;
    private _schadenArtAndere: string;
    private _documentIdsToConnect: Array<string> = [];
    private _beitrag: TSBeitrag | undefined;
    private _wiederherstellungskosten: number;
    private _ermittelterSchaden: number | undefined;

    public get schadendatum(): moment.Moment | undefined {
        return this._schadendatum;
    }

    public set schadendatum(value: moment.Moment | undefined) {
        this._schadendatum = value;
    }

    public get bemerkung(): string {
        return this._bemerkung;
    }

    public set bemerkung(value: string) {
        this._bemerkung = value;
    }

    public get status(): TSVersicherungsbeitragStatus {
        return this._status;
    }

    public set status(value: TSVersicherungsbeitragStatus) {
        this._status = value;
    }

    public get policeNummer(): string | undefined {
        return this._policeNummer;
    }

    public set policeNummer(value: string | undefined) {
        this._policeNummer = value;
    }

    public get externalSchadenNr(): string {
        return this._externalSchadenNr;
    }

    public set externalSchadenNr(value: string) {
        this._externalSchadenNr = value;
    }

    public get schadenArt(): TSSchadenArt {
        return this._schadenArt;
    }

    public set schadenArt(value: TSSchadenArt) {
        this._schadenArt = value;
    }

    public get gemeinde(): TSGemeinde | undefined {
        return this._gemeinde;
    }

    public set gemeinde(value: TSGemeinde | undefined) {
        this._gemeinde = value;
    }

    public get versicherung(): TSVersicherung {
        return this._versicherung;
    }

    public set versicherung(value: TSVersicherung) {
        this._versicherung = value;
    }

    public get gesuchsteller(): TSGesuchsteller {
        return this._gesuchsteller;
    }

    public set gesuchsteller(value: TSGesuchsteller) {
        this._gesuchsteller = value;
    }

    public get schadenArtAndere(): string {
        return this._schadenArtAndere;
    }

    public set schadenArtAndere(value: string) {
        this._schadenArtAndere = value;
    }

    public get documentIdsToConnect(): Array<string> {
        return this._documentIdsToConnect;
    }

    public set documentIdsToConnect(value: Array<string>) {
        this._documentIdsToConnect = value;
    }

    public get beitrag(): TSBeitrag | undefined {
        return this._beitrag;
    }

    public set beitrag(value: TSBeitrag | undefined) {
        this._beitrag = value;
    }

    public get wiederherstellungskosten(): number {
        return this._wiederherstellungskosten;
    }

    public set wiederherstellungskosten(value: number) {
        this._wiederherstellungskosten = value;
    }

    public get ermittelterSchaden(): number | undefined {
        return this._ermittelterSchaden;
    }

    public set ermittelterSchaden(value: number | undefined) {
        this._ermittelterSchaden = value;
    }

    deserialize(restObject: any): TSVersicherungsbeitrag {
        super.deserialize(restObject);
        this.schadendatum = DateUtil.localDateToMoment(restObject.schadendatum);
        this.bemerkung = restObject.bemerkung;
        this.schadenArt = restObject.schadenArt;
        this.externalSchadenNr = restObject.externalSchadenNr;
        this.policeNummer = restObject.policeNummer;
        this.status = restObject.status;
        this.schadenArtAndere = restObject.schadenArtAndere;
        this.wiederherstellungskosten = restObject.wiederherstellungskosten;
        this.ermittelterSchaden = restObject.ermittelterSchaden;
        if (restObject.gesuchsteller) {
            this.gesuchsteller = new TSGesuchsteller().deserialize(restObject.gesuchsteller);
        }
        if (restObject.gemeinde) {
            this.gemeinde = new TSGemeinde().deserialize(restObject.gemeinde);
        }
        if (restObject.versicherung) {
            this.versicherung = new TSVersicherung().deserialize(restObject.versicherung);
        }
        if (restObject.beitrag) {
            this.beitrag = new TSBeitrag().deserialize(restObject.beitrag);
        }
        return this;
    }

    serialize(restObject: any): any {
        super.serialize(restObject);
        restObject.bemerkung = this.bemerkung;
        if (this.schadendatum) {
            restObject.schadendatum = DateUtil.momentToLocalDate(this.schadendatum);
        }
        restObject.bemerkung = this.bemerkung;
        restObject.schadenArt = this.schadenArt;
        restObject.externalSchadenNr = this.externalSchadenNr;
        restObject.policeNummer = this.policeNummer;
        restObject.status = this.status;
        restObject.wiederherstellungskosten = this.wiederherstellungskosten;
        restObject.gesuchsteller = this.serializeOrInit(this.gesuchsteller, restObject.gesuchsteller);
        restObject.gemeinde = this.serializeOrInit(this.gemeinde, restObject.gemeinde);
        restObject.versicherung = this.serializeOrInit(this.versicherung, restObject.versicherung);
        restObject.schadenArtAndere = this.schadenArtAndere;
        restObject.beitrag = this.serializeOrInit(this.beitrag, restObject.beitrag);
        restObject.ermittelterSchaden = this.ermittelterSchaden;

        return restObject;
    }

    public init(): TSVersicherungsbeitrag {
        this.status = TSVersicherungsbeitragStatus.NEU;
        return this;
    }

    public getAnrechenbarerSchaden(prozentsatz: number = 100): number {
        let schaden = this.ermittelterSchaden || 0;
        return (schaden - (this.gesuchsteller.getEinkommensabzug() + this.gesuchsteller.getVermoegenssabzug(this.schadendatum, schaden))) * (prozentsatz / 100);

    }

}



// WEBPACK FOOTER //
// ./src/app/models/TSVersicherungsbeitrag.ts